import React, { useState } from 'react';
import { documentAPI } from '../../../services/api';
import DocumentUploadFiltered from '../DocumentUploadFiltered';
import GenerateDocument from '../GenerateDocument';
import DocumentViewModal from '../DocumentViewModal';
import DocumentEditor from '../DocumentEditor';
import { extractPdfHtml } from '../pdfTextExtract';

const typeLabels = ['Photo', 'Contract', 'Permit', 'Estimate', 'Other'];
const typeClasses = ['photo', 'contract', 'permit', 'estimate', 'other'];

// Font Awesome 7 solid icon per document type / file (canonical names only —
// legacy aliases like fa-edit render blank in FA7).
const fileIcon = (doc) => {
    if (doc.contentType === 'application/pdf' || /\.pdf$/i.test(doc.fileName || '')) return 'fa-file-pdf';
    if (/\.(png|jpe?g|gif|webp)$/i.test(doc.fileName || '')) return 'fa-file-image';
    switch (doc.documentType) {
        case 1: return 'fa-file-contract';
        case 3: return 'fa-file-invoice-dollar';
        case 2: return 'fa-file-lines';
        default: return 'fa-file';
    }
};

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const isPdf = (doc) =>
    doc.contentType === 'application/pdf' || /\.pdf$/i.test(doc.fileName || '');

const DocumentsTab = ({ documents, projectId, onRefresh }) => {
    const [viewDoc, setViewDoc] = useState(null);
    const [editDoc, setEditDoc] = useState(null);   // { doc, autoSign, initialHtml }
    const [preparing, setPreparing] = useState(false);

    const handleDownload = async (id) => {
        try {
            const { data } = await documentAPI.getDownloadUrl(id);
            window.open(data.downloadUrl, '_blank');
        } catch (err) {
            console.error('Failed to get download URL:', err);
        }
    };

    const handleDelete = async (id, fileName) => {
        if (!window.confirm(`Delete "${fileName}"?`)) return;
        try {
            await documentAPI.delete(id);
            onRefresh();
        } catch (err) {
            console.error('Failed to delete document:', err);
        }
    };

    // Open the rich editor. Generated docs load their stored HTML; uploaded PDFs
    // have their text extracted client-side into the same editor.
    const openEditor = async (doc, autoSign) => {
        if (doc.editable) { setEditDoc({ doc, autoSign, initialHtml: null }); return; }
        if (!isPdf(doc)) return;
        setPreparing(true);
        try {
            const res = await documentAPI.getContent(doc.id);
            const html = await extractPdfHtml(res.data);
            setEditDoc({ doc, autoSign, initialHtml: html });
        } catch (err) {
            console.error('Failed to prepare PDF for editing:', err);
            window.alert('Could not open this PDF for editing.');
        } finally {
            setPreparing(false);
        }
    };
    const openEdit = (doc) => openEditor(doc, false);
    const handleSign = (doc) => openEditor(doc, true);
    const canEdit = (doc) => doc.editable || isPdf(doc);

    return (
        <div>
            <div className="tab-header">
                <h3>Documents ({documents.length})</h3>
            </div>

            <GenerateDocument projectId={projectId} onGenerated={onRefresh} />
            <DocumentUploadFiltered projectId={projectId} onUploadComplete={onRefresh} />

            {documents.length === 0 ? (
                <div className="tab-empty">
                    <i className="fas fa-file-alt"></i>
                    <p>No documents uploaded yet</p>
                    <p>Upload contracts, permits, estimates, and other files</p>
                </div>
            ) : (
                <div className="doc-list">
                    <table className="doc-table">
                        <thead>
                            <tr>
                                <th>File Name</th>
                                <th>Type</th>
                                <th>Size</th>
                                <th>Uploaded</th>
                                <th className="doc-col-download">Download</th>
                                <th className="doc-col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documents.map(doc => (
                                <tr key={doc.id}>
                                    <td>
                                        <button
                                            className={`doc-name-link ${typeClasses[doc.documentType] || 'other'}`}
                                            onClick={() => setViewDoc(doc)}
                                            title="View document"
                                        >
                                            <i className={`fas ${fileIcon(doc)}`} />
                                            <span>{doc.fileName}</span>
                                        </button>
                                    </td>
                                    <td>
                                        <span className={`doc-type-badge ${typeClasses[doc.documentType] || 'other'}`}>
                                            {typeLabels[doc.documentType] || 'Other'}
                                        </span>
                                    </td>
                                    <td>{formatFileSize(doc.fileSize)}</td>
                                    <td>{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                                    <td className="doc-col-download">
                                        <button
                                            onClick={() => handleDownload(doc.id)}
                                            className="doc-icon-btn download"
                                            title="Download"
                                        >
                                            <i className="fas fa-download" />
                                        </button>
                                    </td>
                                    <td className="doc-actions">
                                        <button onClick={() => setViewDoc(doc)} className="doc-icon-btn" title="View">
                                            <i className="fas fa-eye" />
                                        </button>
                                        {canEdit(doc) && (
                                            <button onClick={() => openEdit(doc)} className="doc-icon-btn" title="Edit">
                                                <i className="fas fa-pen-to-square" />
                                            </button>
                                        )}
                                        {canEdit(doc) && (
                                            <button onClick={() => handleSign(doc)} className="doc-icon-btn sign" title="Add signature">
                                                <i className="fas fa-signature" />
                                            </button>
                                        )}
                                        <button onClick={() => handleDelete(doc.id, doc.fileName)} className="doc-icon-btn danger" title="Delete">
                                            <i className="fas fa-trash-can" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {viewDoc && (
                <DocumentViewModal
                    documentId={viewDoc.id}
                    fileName={viewDoc.fileName}
                    onClose={() => setViewDoc(null)}
                />
            )}

            {preparing && (
                <div className="doc-preparing-overlay">
                    <div className="doc-preparing-box">
                        <i className="fas fa-spinner fa-spin" /> Preparing document…
                    </div>
                </div>
            )}

            {editDoc && (
                <DocumentEditor
                    documentId={editDoc.doc.id}
                    fileName={editDoc.doc.fileName}
                    autoSign={editDoc.autoSign}
                    initialHtml={editDoc.initialHtml}
                    onClose={() => setEditDoc(null)}
                    onSaved={onRefresh}
                />
            )}
        </div>
    );
};

export default DocumentsTab;
